import { useLazyQuery } from '@apollo/client';
import { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import useTable, { TableState } from '../../../../components/Table/useTable';
import { SEARCH_RECEIVE_EXCEPTIONS } from '../../../../services/receive-exception/queries';
import {
  transformReceiveExceptionError,
  transformSearchFilters,
  transformSearchResults,
} from '../helper';
import { ReceiveExceptionSearchResult, SearchFilterValues } from '../types';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SEARCH_FILTERS,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  DEFAULT_VISIBLE_COLUMNS,
} from './config';
import { ExceptionTypeOptionsProps } from '../config';
import { useQueryAppConfig } from '../appConfigUtils';

export type SearchExceptionState = TableState<ReceiveExceptionSearchResult> & {
  filters: SearchFilterValues;
  loading: boolean;
  error: string;
  setError: (error: string) => void;
  onSearch: (filters: SearchFilterValues) => Promise<void>;
  fetchConfigError?: string;
  exceptionTypes: ExceptionTypeOptionsProps[];
  loadingResolverConfig?: boolean;
};

const TABLE_ID = 'receive-exception-search-v2';

export default function useSearchExceptions(): SearchExceptionState {
  const tableState = useTable<ReceiveExceptionSearchResult>({
    tableId: TABLE_ID,
    pageSize: DEFAULT_PAGE_SIZE,
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    sortBy: DEFAULT_SORT_BY,
    sortOrder: DEFAULT_SORT_ORDER,
  });
  const [filters, setFilters] = useState<SearchFilterValues>(DEFAULT_SEARCH_FILTERS);
  const [error, setError] = useState<string>('');

  // Fetch exception type and resolver group info from AWS AppConfig
  const { fetchConfigError, exceptionTypes, loadingResolverConfig } = useQueryAppConfig(true);

  const [searchExceptions, { loading: searching }] = useLazyQuery(SEARCH_RECEIVE_EXCEPTIONS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onCompleted: (result) => {
      tableState.setItems(
        transformSearchResults(result.searchReceiveExceptions.searchResults, exceptionTypes)
      );
      tableState.setPageCount(
        Math.ceil(result.searchReceiveExceptions.totalHits / tableState.pageSize)
      );
      tableState.setTotalCount(result.searchReceiveExceptions.totalHits);
    },
    onError: (e) => {
      setError(transformReceiveExceptionError(e));
    },
  });

  useDeepCompareEffect(() => {
    searchExceptions({
      variables: {
        request: {
          filters: transformSearchFilters(filters),
          sortBy: tableState.sortBy,
          sortOrder: tableState.sortOrder,
          from: (tableState.pageIndex - 1) * tableState.pageSize,
          size: tableState.pageSize,
        },
      },
    });
  }, [filters, tableState.pageIndex, tableState.sortBy, tableState.sortOrder, tableState.pageSize]);

  const handleSearch = async (updatedFilters: SearchFilterValues) => {
    tableState.setPageIndex(1);
    setFilters(updatedFilters);
  };

  return {
    ...tableState,
    filters,
    error,
    setError,
    loading: searching,
    onSearch: handleSearch,
    fetchConfigError,
    exceptionTypes,
    loadingResolverConfig,
  };
}
